/**
 * currentPrincipal — WHO the session signed in as, as `ensureIdentity()` hands it
 * back.
 *
 * Signing in is one await (`ensureIdentity()`), and it now RETURNS the identity.
 * That value is the app's only upstream answer to "who am I": it is known the
 * moment the barrier settles, before any document has been read.
 *
 * WHAT IT IS FOR — display and logging, nothing else. **No call takes it**: a
 * session belongs to one user, so placement is named by scope alone
 * (`createEntityDoc(scope)`, `listMyEntityDocs(scope)`). Handing this value back
 * to the data layer would re-create the parameter the surface deliberately
 * removed, so it must never travel into an SDK call.
 *
 * WHAT IT IS NOT — it is NOT the id space the app's own entities live in. A
 * UserProfile's id is its document NURI, and a Participation's `fp:user` carries
 * that NURI; this principal is a third space, and there is NO join between them.
 * A profile is Festipod's own object and this identity says nothing about it:
 * **my profile is the profile document I own** (`listMyEntityDocs('protected')`,
 * resolved in `FestipodDataContext`). Never compare this value to an entity id,
 * and never match it against a profile field to decide who the current user is.
 *
 * WHY A MODULE STORE and not a React context: `AuthGate` — the component that
 * makes the await — is mounted INSIDE `FestipodDataProvider`, so a context it
 * published would be invisible to the very consumer that needs it. A plain
 * module singleton observed with `useSyncExternalStore` is independent of
 * provider order, exactly like `data/pendingQueries.ts`.
 */

import { useSyncExternalStore } from 'react';
import type { PrincipalId } from '@ng-eventually/polyfill';

/** `''` means "signing in has not settled yet" — never "no identity". */
let principal: string = '';
const listeners = new Set<() => void>();

/** Record the identity `ensureIdentity()` returned. Idempotent per value. */
export function setCurrentPrincipal(id: PrincipalId): void {
  if (principal === id) return;
  principal = id;
  for (const l of listeners) l();
}

/** The signed-in principal, or `''` while the one identity await is pending. */
export function getCurrentPrincipal(): string {
  return principal;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Subscribe a component to the signed-in principal. The snapshot is a primitive
 * string, stable between real changes — no render loop.
 */
export function useCurrentPrincipal(): string {
  return useSyncExternalStore(subscribe, getCurrentPrincipal, getCurrentPrincipal);
}
